import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import {
  deleteCruiseLine,
  fetchCruiseLines,
  refreshCruiseLineList,
} from "src/store/reducers/cruiseLines";
import ConfirmationModal from "src/layouts/modals/ConfirmationModal";
import { toast } from "react-toastify";
import CruiseLineFormCanvas from "./CruiseLineFormCanvas";
import { FormikAPIErrorHandler } from "src/utils/errorUtil";
import BottomPagination from "src/jsx/customComponents/pagination/BottomPagination";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Button } from "react-bootstrap";
import CustomInputWithLabel from "src/layouts/form/CustomInputWithLabel";

const ListCruiseLines = () => {
  const dispatch = useAppDispatch();
  const cruiseLineList = useAppSelector((state) => state.cruiseLines.data);
  const cruiseLineListStatus = useAppSelector((state) => state.cruiseLines.status);
  const [editId, setEditId] = useState("");
  const [deleteCruiseLineId, setDeleteCruiseLineId] = useState<string>();
  const [page, setPage] = useState(1);

  const [pending, setPending] = useState(false);
  const keywordRef = useRef("");

  const {
    handleChange,
    values,
    handleSubmit,
  } = useFormik({
    initialValues: {
      keyword: "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      keyword: Yup.string(),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      setPending(true);
      keywordRef.current = values.keyword;
      setPage(1);
      dispatch(fetchCruiseLines({ page: 1, keyword: values.keyword }));
      setPending(false);
    },
  });

  const retry = useCallback(() => {
    dispatch(fetchCruiseLines({ page, keyword: keywordRef.current }));
  }, [dispatch, page]);

  const cancelDelete = () => {
    setDeleteCruiseLineId(undefined);
  };

  const confirmDelete = useCallback(async () => {
    if (deleteCruiseLineId) {
      try {
        const data = await dispatch(deleteCruiseLine(deleteCruiseLineId)).unwrap();
        toast.success("Cruise line is deleted successfully");
        setDeleteCruiseLineId(undefined);
        setPage(1);
        retry();
      } catch (e: any) {
        FormikAPIErrorHandler(e.message, undefined, toast.error);
      }
    }
  }, [deleteCruiseLineId, dispatch, retry]);

  useEffect(() => {
    if (cruiseLineListStatus === "idle") {
      retry();
    }
  }, [cruiseLineListStatus, retry]);

  useEffect(() => {
    return () => {
      dispatch(refreshCruiseLineList());
    };
  }, [dispatch]);

  const setPageAndFetch = useCallback(
    (newPage: number) => {
      setPage(newPage);
      dispatch(fetchCruiseLines({ page: newPage, keyword: keywordRef.current }));
    },
    [dispatch]
  );

  return (
    <>
      <div className="container-fluid">
        <div className="row">
          <div className="col-xl-12 col-xxl-12">
            <div className="card h-auto">
              <form onSubmit={handleSubmit}>
                <div className="row container-fluid">
                  <div className="col-xl-3 mb-3">
                    <CustomInputWithLabel
                      id="keyword"
                      placeholder={"Search"}
                      handleChange={handleChange}
                      value={values.keyword}
                    />
                  </div>
                  <div className="col-xl-2 mb-3">
                    <Button
                      className="form-control"
                      disabled={pending}
                      type="submit"
                    >
                      {pending ? "Loading" : "Submit"}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="container-fluid">
        <div className="row">
          <div className="col-xl-12 col-xxl-12">
            <div className="card h-auto">
              <div className="card-header">
                <h4 className="heading mb-0">Cruise Lines</h4>
                <Link
                  to={"#"}
                  className="btn btn-primary btn-sm"
                  data-bs-toggle="offcanvas"
                  onClick={() => setEditId("Add")}
                >
                  + Add Cruise Line
                </Link>
              </div>
              <div className="card-body ">
                {cruiseLineListStatus === "success" ? (
                  cruiseLineList?.results && cruiseLineList?.results.length > 0 ? (
                    <div className="table-responsive active-projects">
                      <div
                        id="projects-tbl_wrapper"
                        className="dataTables_wrapper no-footer"
                      >
                        <table
                          id="projects-tbl"
                          className="table table-hover dataTable no-footer mb-0"
                        >
                          <thead>
                            <tr>
                              <th>Cruise Line Name</th>
                              <th>Cruise Line Code</th>
                              <th>Contact Person</th>
                              <th>Contact No.</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cruiseLineList?.results?.map((cruiseLine, index) => (
                              <tr key={cruiseLine.id}>
                                <td>{cruiseLine.cruise_line_name}</td>
                                <td>{cruiseLine.cruise_line_code}</td>
                                <td>{cruiseLine.contact_person_name}</td>
                                <td>{cruiseLine.contact_number}</td>
                                <td>
                                  <NavLink
                                    className="btn btn-warning shadow btn-xs sharp me-1"
                                    to={`/cruise-lines/view/${cruiseLine.id}`}
                                    title="View"
                                  >
                                    <i className={`fas fa-paper-plane`} />
                                  </NavLink>{" "}
                                  <NavLink
                                    className="btn btn-primary shadow btn-xs sharp me-1"
                                    onClick={() => setEditId(cruiseLine.id)}
                                    to={"#"}
                                    title="Edit"
                                  >
                                    <i className={`fas fa-pen`} />
                                  </NavLink>{" "}
                                  <NavLink
                                    className="btn btn-danger shadow btn-xs sharp"
                                    onClick={() => {
                                      setDeleteCruiseLineId(cruiseLine.id);
                                    }}
                                    to={`#`}
                                    title="Delete"
                                  >
                                    <i className={`fas fa-x`} />
                                  </NavLink>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {cruiseLineList?.count ? (
                          <BottomPagination
                            count={cruiseLineList?.count}
                            page={page}
                            setPageAndFetch={setPageAndFetch}
                          />
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center">
                      <strong>NA</strong>
                    </div>
                  )
                ) : cruiseLineListStatus === "pending" ? (
                  <div className="d-flex align-items-center">
                    <strong>Loading...</strong>
                    <div
                      className="spinner-border ms-auto"
                      role="status"
                      aria-hidden="true"
                    ></div>
                  </div>
                ) : cruiseLineListStatus === "error" ? (
                  <div className="d-flex align-items-center">
                    <button
                      type="button"
                      className="btn btn-warning"
                      onClick={retry}
                    >
                      Retry
                    </button>{" "}
                    &nbsp;
                    <strong onClick={retry}>Please try again </strong>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConfirmationModal
        show={typeof deleteCruiseLineId !== "undefined"}
        handleClose={cancelDelete}
        confirmClose={confirmDelete}
      />
      <CruiseLineFormCanvas
        show={editId !== ""}
        hide={() => setEditId("")}
        editId={editId}
        Title={editId === "Add" ? "Add Cruise Line" : "Edit Cruise Line"}
        cruiseLineData={cruiseLineList?.results?.find((cl) => cl.id === editId)}
      />
    </>
  );
};

export default ListCruiseLines;
